import { ApolloLink, FetchResult, Observable } from '@apollo/client'
import { isUnauthenticatedError } from './is-unauthenticated-error'

export type SessionExpiryLinkOptions = {
  readonly isEnabled: () => boolean
  readonly refresh: () => Promise<unknown>
  readonly onSessionExpired: (operationName: string) => void
}

export function createSessionExpiryLink({
  isEnabled,
  refresh,
  onSessionExpired,
}: SessionExpiryLinkOptions): ApolloLink {
  return new ApolloLink(function (operation, forward) {
    if (!isEnabled()) return forward(operation)

    return new Observable<FetchResult>((observer) => {
      let subscription: { unsubscribe: () => void } | undefined
      let closed = false
      let retrying = false

      function expire(deliver: () => void) {
        onSessionExpired(operation.operationName)
        deliver()
      }

      // Resends exactly once with a freshly refreshed token; the auth link reads it at send time.
      function retry(deliverFailure: () => void) {
        retrying = true
        subscription?.unsubscribe()
        refresh()
          .then(() => {
            if (closed) return
            retrying = false
            attempt(true)
          })
          .catch(() => {
            if (!closed) expire(deliverFailure)
          })
      }

      function attempt(isRetry: boolean) {
        subscription = forward(operation).subscribe({
          next: (result) => {
            if (!isUnauthenticatedError({ graphQLErrors: result.errors })) return observer.next(result)
            if (isRetry) return expire(() => observer.next(result))
            retry(() => {
              observer.next(result)
              observer.complete()
            })
          },
          error: (networkError) => {
            if (!isUnauthenticatedError({ networkError })) return observer.error(networkError)
            if (isRetry) return expire(() => observer.error(networkError))
            retry(() => observer.error(networkError))
          },
          complete: () => {
            if (!retrying) observer.complete()
          },
        })
      }

      attempt(false)

      return () => {
        closed = true
        subscription?.unsubscribe()
      }
    })
  })
}
